import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug, faPlus, faCopy, faKey, faTrash, faPause, faPlay, faPen } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import api, { SERVER_URL } from '../../utils/api';
import '../../styles/App.css';
import '../../styles/integrations.css';

/**
 * Outside apps that may post notifications into employees' chats
 * (server: routes/integrations.js).
 *
 * The key is shown once, when an app is made or given a new one, because the
 * server keeps only its hash. Losing it means making a new one, which also
 * switches the old one off - that is the point.
 */

const NOTIFY_URL = `${SERVER_URL}/api/integrations/notify`;

const EMPTY = { name: '', iconUrl: '', allowedDomains: '', description: '' };

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
}) : 'Never');

const copy = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        Swal.fire({ toast: true, position: 'top-end', timer: 1500, showConfirmButton: false, icon: 'success', title: 'Copied' });
    } catch {
        Swal.fire('Copy failed', 'Select the text and copy it by hand.', 'warning');
    }
};

const sample = (key) => `curl -X POST ${NOTIFY_URL} \\
  -H "Content-Type: application/json" \\
  -H "X-Api-Key: ${key || '<your key>'}" \\
  -d '{
    "emails": ["employee@company.com", "vendor@supplier.com"],
    "text": "You got a discussion notification",
    "link": "https://your-app.com/discussions/123",
    "linkLabel": "Open discussion",
    "referenceId": "comment-123"
  }'`;

const Integrations = () => {
    const [apps, setApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [off, setOff] = useState(false);
    // null: no form. { ...fields, _id? }: creating, or editing that app.
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    // The key just minted, shown once: { appName, key }.
    const [newKey, setNewKey] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/integrations/apps');
            setApps(res.data);
            setOff(false);
        } catch (err) {
            if (err.response?.status === 404) setOff(true);
            else Swal.fire('Error', err.response?.data?.message || 'Could not load the apps.', 'error');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        const body = {
            name: form.name,
            iconUrl: form.iconUrl,
            description: form.description,
            allowedDomains: form.allowedDomains
        };
        try {
            if (form._id) {
                await api.put(`/integrations/apps/${form._id}`, body);
            } else {
                const res = await api.post('/integrations/apps', body);
                setNewKey({ appName: res.data.app.name, key: res.data.apiKey });
            }
            setForm(null);
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not save the app.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (app) => {
        try {
            await api.put(`/integrations/apps/${app._id}`, { active: !app.active });
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not update the app.', 'error');
        }
    };

    const rotate = async (app) => {
        const ok = await Swal.fire({
            title: `New key for ${app.name}?`,
            text: 'The current key stops working immediately. The app must be updated with the new one.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Make a new key'
        });
        if (!ok.isConfirmed) return;
        try {
            const res = await api.post(`/integrations/apps/${app._id}/rotate-key`);
            setNewKey({ appName: app.name, key: res.data.apiKey });
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not make a new key.', 'error');
        }
    };

    const remove = async (app) => {
        const ok = await Swal.fire({
            title: `Remove ${app.name}?`,
            text: 'Its key stops working. Notifications it already sent stay in people\'s chats.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Remove',
            confirmButtonColor: '#dc2626'
        });
        if (!ok.isConfirmed) return;
        try {
            await api.delete(`/integrations/apps/${app._id}`);
            load();
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove the app.', 'error');
        }
    };

    const field = (key) => ({
        value: form[key],
        onChange: (e) => setForm((f) => ({ ...f, [key]: e.target.value }))
    });

    return (
        <div className="attendance-container fade-in" style={{ paddingBottom: '40px' }}>
            <h1 className="page-title">
                <FontAwesomeIcon icon={faPlug} style={{ marginRight: '10px' }} />
                Integrations
            </h1>
            <p className="text-muted" style={{ marginTop: '-6px', marginBottom: '20px' }}>
                Outside apps that can send notifications into an employee's chats. A key can only send;
                it cannot read any chat.
            </p>

            {loading && <div className="text-muted">Loading…</div>}

            {!loading && off && (
                <div className="control-card intg-card">
                    Integrations are switched off on the server. Set <code>INTEGRATIONS_ENABLED=true</code> in
                    the server's environment and restart it to use this page.
                </div>
            )}

            {!loading && !off && (
                <>
                    {newKey && (
                        <div className="control-card intg-card intg-key">
                            <strong>API key for {newKey.appName}</strong>
                            <p className="text-muted">
                                Copy it now. It will not be shown again. If it is lost, make a new one.
                            </p>
                            <div className="intg-key-row">
                                <code>{newKey.key}</code>
                                <button type="button" className="gts-btn secondary" onClick={() => copy(newKey.key)}>
                                    <FontAwesomeIcon icon={faCopy} /> Copy
                                </button>
                            </div>
                            <pre className="intg-sample">{sample(newKey.key)}</pre>
                            <button type="button" className="gts-btn secondary" onClick={() => setNewKey(null)}>
                                I have saved it
                            </button>
                        </div>
                    )}

                    {form ? (
                        <form className="control-card intg-card intg-form" onSubmit={save}>
                            <strong>{form._id ? `Edit ${form.name}` : 'New app'}</strong>
                            <label>
                                Name, shown as the chat's title
                                <input className="custom-input" maxLength={60} required {...field('name')} />
                            </label>
                            <label>
                                Allowed link domains, comma separated (subdomains are allowed too)
                                <input className="custom-input" placeholder="app.example.com" required {...field('allowedDomains')} />
                            </label>
                            <label>
                                Icon url (https, optional)
                                <input className="custom-input" type="url" placeholder="https://…/icon.png" {...field('iconUrl')} />
                            </label>
                            <label>
                                Notes (optional, only admins see these)
                                <input className="custom-input" maxLength={300} {...field('description')} />
                            </label>
                            <div className="intg-actions">
                                <button type="button" className="gts-btn secondary" onClick={() => setForm(null)}>Cancel</button>
                                <button type="submit" className="gts-btn primary" disabled={saving}>
                                    {form._id ? 'Save' : 'Create and show key'}
                                </button>
                            </div>
                        </form>
                    ) : (
                        <button type="button" className="gts-btn primary" style={{ marginBottom: 16 }} onClick={() => setForm(EMPTY)}>
                            <FontAwesomeIcon icon={faPlus} /> Add app
                        </button>
                    )}

                    {!apps.length && !form && <div className="text-muted">No apps yet.</div>}

                    <div className="intg-list">
                        {apps.map((app) => (
                            <div key={app._id} className={`control-card intg-card intg-app ${app.active ? '' : 'is-paused'}`}>
                                <div className="intg-app-head">
                                    {app.iconUrl
                                        ? <img src={app.iconUrl} alt="" className="intg-icon" />
                                        : <div className="intg-icon"><FontAwesomeIcon icon={faPlug} /></div>}
                                    <div>
                                        <strong>{app.name}</strong>
                                        {!app.active && <span className="intg-pill">Paused</span>}
                                        <div className="text-muted intg-meta">
                                            {app.allowedDomains.join(', ')} · key {app.keyHint}
                                        </div>
                                        <div className="text-muted intg-meta">
                                            {app.sentCount} sent · last used {when(app.lastUsedAt)}
                                        </div>
                                        {app.description && <div className="intg-meta">{app.description}</div>}
                                    </div>
                                </div>
                                <div className="intg-actions">
                                    <button type="button" className="gts-btn secondary" onClick={() => setForm({
                                        _id: app._id,
                                        name: app.name,
                                        iconUrl: app.iconUrl || '',
                                        description: app.description || '',
                                        allowedDomains: app.allowedDomains.join(', ')
                                    })}>
                                        <FontAwesomeIcon icon={faPen} /> Edit
                                    </button>
                                    <button type="button" className="gts-btn secondary" onClick={() => toggleActive(app)}>
                                        <FontAwesomeIcon icon={app.active ? faPause : faPlay} /> {app.active ? 'Pause' : 'Resume'}
                                    </button>
                                    <button type="button" className="gts-btn secondary" onClick={() => rotate(app)}>
                                        <FontAwesomeIcon icon={faKey} /> New key
                                    </button>
                                    <button type="button" className="gts-btn danger" onClick={() => remove(app)}>
                                        <FontAwesomeIcon icon={faTrash} /> Remove
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="control-card intg-card">
                        <strong>Sending a notification</strong>
                        <p className="text-muted">
                            <code>POST {NOTIFY_URL}</code> with the app's key in an <code>X-Api-Key</code> header.
                            Name the people with <code>email</code> / <code>emails</code> (employees and guests) or{' '}
                            <code>employeeId</code> / <code>employeeIds</code> (employees) or{' '}
                            <code>guestId</code> / <code>guestIds</code> (the EXT-XXXXX on a guest's profile), up to
                            50 per request, and
                            send <code>text</code> (up to 1000 characters). <code>title</code>, <code>link</code>,{' '}
                            <code>linkLabel</code> and <code>referenceId</code> are optional. The link must be https
                            on one of the app's domains. A second notification with the same <code>referenceId</code>{' '}
                            is not delivered again. Notifications appear only in the portal; nothing is emailed.
                        </p>
                        <p className="text-muted">
                            The reply lists every person with a <code>status</code> (<code>sent</code>,{' '}
                            <code>duplicate</code>, <code>not_found</code>, <code>ambiguous</code>,{' '}
                            <code>invalid</code> or <code>error</code>) and the <code>messageId</code> of their
                            notification. 400 means bad input, 401 a bad key, and 429 more than 300 notifications
                            a minute.
                        </p>
                        <pre className="intg-sample">{sample()}</pre>
                    </div>
                </>
            )}
        </div>
    );
};

export default Integrations;
